import {
  HealthMetricValue,
  HealthMetricValueV3,
} from '@leagueplatform/dashboard-api';
import {
  NUMERIC_YEAR_MONTH_DAY,
  useFormatDateByUserProfileLocation,
} from '@leagueplatform/web-common';
import { compareAsc } from 'date-fns';
import type {
  MasonryChartAggregationBucketType,
  MasonryChartDataPoint,
} from '#types/masonry-chart.type.ts';
import { groupByKey } from './group-by-key';

/**
 * Aggregates indicators from multiple readings.
 * Returns `out_of_range` if any reading is `out_of_range`; returns `in_range`
 * only when there is at least one reading and every reading is `in_range`;
 * otherwise returns `undefined`.
 */
export function aggregateIndicators(
  healthMetricsByType: Pick<HealthMetricValueV3Isolate, 'indicator'>[],
): HealthMetricValueV3Isolate['indicator'] {
  const outOfRange = healthMetricsByType.find(
    (m) => m.indicator?.type === 'out_of_range',
  );
  if (outOfRange) {
    return outOfRange.indicator;
  }

  if (
    healthMetricsByType.length > 0 &&
    healthMetricsByType.every((m) => m.indicator?.type === 'in_range')
  ) {
    return healthMetricsByType[0].indicator;
  }

  return undefined;
}

export type HealthMetricData = ReturnType<typeof useHealthMetricValues>;
export type HealthMetricDataV3 = ReturnType<typeof useHealthMetricValuesV3>;

// This is a narrowed type of HealthMetricValueV3 which only includes
// values that are used in the useHealthMetricValuesV3 function.  This makes it easier
// for the caller to understand which fields are required for this function to work.
export type HealthMetricValueV3Isolate = Pick<
  HealthMetricValueV3,
  'timestamp' | 'value' | 'label'
> & {
  metricComponent: {
    data: Pick<
      HealthMetricValueV3['metricComponent']['data'],
      'name' | 'order' | 'unit'
    >;
  };
} & Pick<MasonryChartDataPoint, 'indicator' | 'aggregationStrategyLabel'>;

export type AggregationBucketType = MasonryChartAggregationBucketType;

const BUCKET_DATE_FORMAT: Record<
  MasonryChartAggregationBucketType,
  Intl.DateTimeFormatOptions
> = {
  daily: NUMERIC_YEAR_MONTH_DAY,
  monthly: { year: 'numeric', month: 'numeric' },
  yearly: { year: 'numeric' },
};

export function useHealthMetricValuesV3(
  healthMetrics: HealthMetricValueV3Isolate[],
  bucketType: MasonryChartAggregationBucketType = 'daily',
) {
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();

  // Group health metrics by bucket key (day/month/year per `bucketType`).
  // Readings sharing a bucket key collapse into a single per-type datum below,
  // ignoring within-bucket day/time when bucketType is wider than 'daily'.
  const groupedByDate = groupByKey(
    (item) =>
      formatDateWithUserProfileLocation(
        item.timestamp,
        BUCKET_DATE_FORMAT[bucketType],
      ),
    healthMetrics,
  );

  const consolidatedHealthMetrics = Object.entries(groupedByDate)
    .map(([, healthMetricsByDate]) => {
      // Group health metrics within each date by metric type.

      const representativeTimestampForDay = healthMetricsByDate[0].timestamp;

      const groupedByMetricType = groupByKey(
        (item) => item.metricComponent.data.name,
        healthMetricsByDate,
      );

      // Iterate over each health metric within a day.
      const healthMetricsMinMax = Object.entries(groupedByMetricType).map(
        ([type, healthMetricsByType]) => {
          let value: number | [number, number] | null = null;
          let label:
            | string
            | [string | undefined, string | undefined]
            | undefined;
          const values = healthMetricsByType
            .map((m) => m.value)
            .filter((v): v is number => v !== null);

          if (values.length > 1) {
            const min = Math.min(...values);
            const max = Math.max(...values);

            const minMetric = healthMetricsByType.find((m) => m.value === min)!;
            const maxMetric = healthMetricsByType.find((m) => m.value === max)!;

            value = [min, max];
            label = [minMetric.label, maxMetric.label];
          } else if (values.length === 1) {
            const metric = healthMetricsByType.find(
              (m) => m.value === values[0],
            )!;
            // eslint-disable-next-line prefer-destructuring
            value = values[0];
            label = metric.label;
          }

          return {
            type,
            timestamp: representativeTimestampForDay,
            value,
            label,
            aggregationStrategyLabel:
              healthMetricsByType[0].aggregationStrategyLabel,
            indicator: aggregateIndicators(healthMetricsByType),
            metric: healthMetricsByType[0].metricComponent,
          };
        },
      );
      return healthMetricsMinMax.sort(
        (a, b) => a.metric.data.order - b.metric.data.order,
      );
    })
    .flat();

  const sortedConsolidatedHealthMetrics = consolidatedHealthMetrics.sort(
    (a, b) => compareAsc(new Date(a.timestamp), new Date(b.timestamp)),
  );

  return sortedConsolidatedHealthMetrics;
}

// Only used by the details2
export function useHealthMetricValues(healthMetrics: HealthMetricValue[]) {
  const healthMetricsIsolate = healthMetrics.map((metric) => ({
    ...metric,
    metricComponent: {
      data: {
        name: metric.config.data.name,
        order: metric.config.data.order,
        unit: metric.config.data.unit,
      },
    },
  }));
  return useHealthMetricValuesV3(healthMetricsIsolate);
}
